// src/pages/AddWritingToIELTS.jsx
import { useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import {
  ArrowRight,
  Save,
  Image as ImageIcon,
  X,
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Highlighter,
} from "lucide-react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Highlight from "@tiptap/extension-highlight";
import { ieltsQuestionsAPI } from "../../services/ieltsService";

/* ------------------------------------------------------------------ */
/* Rich text editor for the question text (Bold / Italic / Underline / Highlight) */
/* ------------------------------------------------------------------ */
function ToolbarBtn({ active, onClick, children, title }) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      className={`p-1.5 rounded-md transition-colors ${
        active
          ? "bg-green-100 text-green-700"
          : "text-gray-500 hover:bg-gray-100"
      }`}
    >
      {children}
    </button>
  );
}

function QuestionTextEditor({ value, onChange }) {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Highlight.configure({ multicolor: false }),
    ],
    content: value,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class:
          "prose prose-sm max-w-none min-h-[100px] px-3 py-2 focus:outline-none",
      },
    },
  });

  if (!editor) return null;

  return (
    <div className="border border-gray-300 rounded-lg focus-within:ring-2 focus-within:ring-green-500">
      <div className="flex gap-1 border-b border-gray-200 p-1 bg-gray-50 rounded-t-lg">
        <ToolbarBtn
          title="Bold"
          active={editor.isActive("bold")}
          onClick={() => editor.chain().focus().toggleBold().run()}
        >
          <Bold className="w-4 h-4" />
        </ToolbarBtn>
        <ToolbarBtn
          title="Italic"
          active={editor.isActive("italic")}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        >
          <Italic className="w-4 h-4" />
        </ToolbarBtn>
        <ToolbarBtn
          title="Underline"
          active={editor.isActive("underline")}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
        >
          <UnderlineIcon className="w-4 h-4" />
        </ToolbarBtn>
        <ToolbarBtn
          title="Highlight"
          active={editor.isActive("highlight")}
          onClick={() => editor.chain().focus().toggleHighlight().run()}
        >
          <Highlighter className="w-4 h-4" />
        </ToolbarBtn>
      </div>
      <EditorContent editor={editor} />
    </div>
  );
}

/* ------------------------------------------------------------------ */

export default function AddWritingToIELTS() {
  const { skillId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [form, setForm] = useState({
    title: "",
    question_text: "",
    min_words: 150,
    max_words: 300,
    sample_answer: "",
    rubric: "",
    points: 10,
    pass_threshold: 60,
    difficulty: "MEDIUM",
    is_active: true,
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleQuestionTextChange = (html) => {
    setForm((prev) => ({ ...prev, question_text: html }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // question_text now comes from the rich text editor as HTML,
    // so an empty value looks like "<p></p>" instead of ""
    const isQuestionTextEmpty =
      !form.question_text || form.question_text.replace(/<[^>]*>/g, "").trim() === "";
    if (isQuestionTextEmpty) {
      setErrors({ question_text: "نص السؤال مطلوب" });
      return;
    }
    setLoading(true);
    setErrors({});
    try {
      const formData = new FormData();
      formData.append("title", form.title);
      formData.append("question_text", form.question_text);
      formData.append("min_words", form.min_words);
      formData.append("max_words", form.max_words);
      formData.append("sample_answer", form.sample_answer);
      formData.append("rubric", form.rubric);
      formData.append("points", form.points);
      formData.append("pass_threshold", form.pass_threshold);
      formData.append("difficulty", form.difficulty);
      formData.append("is_active", form.is_active ? "True" : "False");
      formData.append("ielts_skill", skillId);
      if (imageFile) {
        formData.append("question_image", imageFile);
      }

      await ieltsQuestionsAPI.createWriting(formData);
      navigate(`/dashboard/ielts/skills/${skillId}`);
    } catch (err) {
      setErrors(err.response?.data || { general: "حدث خطأ، حاول مرة أخرى" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <Link
          to={`/dashboard/ielts/skills/${skillId}`}
          className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 transition-colors"
        >
          <ArrowRight className="w-5 h-5" />
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            إضافة سؤال Writing
          </h1>
          <p className="text-gray-500 text-sm">سؤال كتابة مقالة أو فقرة</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="card space-y-5">
        {/* Title */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            عنوان السؤال <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="title"
            value={form.title}
            onChange={handleChange}
            required
            placeholder="مثال: Essay on Technology"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
          />
          {errors.title && (
            <p className="text-red-500 text-xs mt-1">{errors.title}</p>
          )}
        </div>

        {/* Question Text */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            نص السؤال <span className="text-red-500">*</span>
          </label>
          <QuestionTextEditor
            value={form.question_text}
            onChange={handleQuestionTextChange}
          />
          {errors.question_text && (
            <p className="text-red-500 text-xs mt-1">{errors.question_text}</p>
          )}
        </div>

        {/* Question Image */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            صورة السؤال (اختياري)
          </label>
          {imagePreview ? (
            <div className="relative inline-block">
              <img
                src={imagePreview}
                alt="Preview"
                className="w-full max-h-48 object-contain rounded-lg border border-gray-300"
              />
              <button
                type="button"
                onClick={removeImage}
                className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white rounded-full p-1"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <label className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg py-6 cursor-pointer hover:border-green-400 transition-colors">
              <ImageIcon className="w-8 h-8 text-gray-400 mb-2" />
              <span className="text-sm text-gray-500">اضغط لرفع صورة</span>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                className="hidden"
              />
            </label>
          )}
        </div>

        {/* Words Range */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              الحد الأدنى للكلمات <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              name="min_words"
              value={form.min_words}
              onChange={handleChange}
              required
              min={1}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              الحد الأقصى للكلمات <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              name="max_words"
              value={form.max_words}
              onChange={handleChange}
              required
              min={1}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          </div>
        </div>

        {/* Sample Answer */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            الإجابة النموذجية <span className="text-red-500">*</span>
          </label>
          <textarea
            name="sample_answer"
            value={form.sample_answer}
            onChange={handleChange}
            required
            rows={6}
            placeholder="أدخل إجابة نموذجية..."
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500 resize-none"
          />
        </div>

        {/* Rubric */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            معايير التقييم (Rubric) <span className="text-red-500">*</span>
          </label>
          <textarea
            name="rubric"
            value={form.rubric}
            onChange={handleChange}
            required
            rows={4}
            placeholder="أدخل معايير التقييم..."
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500 resize-none"
          />
        </div>

        {/* Points & Pass Threshold */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              النقاط
            </label>
            <input
              type="number"
              name="points"
              value={form.points}
              onChange={handleChange}
              min={1}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              نسبة النجاح (%)
            </label>
            <input
              type="number"
              name="pass_threshold"
              value={form.pass_threshold}
              onChange={handleChange}
              min={0}
              max={100}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          </div>
        </div>

        {/* Difficulty */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            مستوى الصعوبة
          </label>
          <select
            name="difficulty"
            value={form.difficulty}
            onChange={handleChange}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
          >
            <option value="EASY">سهل</option>
            <option value="MEDIUM">متوسط</option>
            <option value="HARD">صعب</option>
          </select>
        </div>

        {/* Active */}
        <div className="flex items-center gap-3">
          <input
            type="checkbox"
            name="is_active"
            id="is_active"
            checked={form.is_active}
            onChange={handleChange}
            className="w-4 h-4 text-green-600"
          />
          <label
            htmlFor="is_active"
            className="text-sm font-medium text-gray-700"
          >
            نشط
          </label>
        </div>

        {errors.general && (
          <p className="text-red-500 text-sm bg-red-50 p-3 rounded-lg">
            {errors.general}
          </p>
        )}

        <button
          type="submit"
          disabled={loading}
          className="w-full flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white py-3 rounded-lg font-medium transition-colors"
        >
          <Save className="w-5 h-5" />
          {loading ? "جاري الحفظ..." : "حفظ السؤال"}
        </button>
      </form>
    </div>
  );
}